/* eslint-disable camelcase */

import Immutable from 'immutable';
import Cookies from 'universal-cookie';
import Resource, { ResourceApi } from '../../resource/Resource';
import { parseQueryString } from '../../web/utilities/utilities';
import { USER_RESOURCE } from '../resourceNames';
import {
  UPDATE_ENROLLED_CLASS_DATA,
  START_FETCH_ENROLLED_CLASS_DATA,
  SET_ENROLMENTS_HISTORY,
  SET_IN_PROGRESS_FALSE,
  SSO_USER_CONTEXT,
  CURRENT_SSO_USER_DETAILS,
  AUTH_SET_CURRENT_COHORT,
  AUTH_SET_CURRENT_COURSE,
  AUTH_SET_CURRENT_PORTAL_NAME,
  SET_CURRENT_USER,
  SET_CURRENT_USER_ACCESS,
  SIGNUP_USER_REQUIRED,
  START_FETCH_SIMPLE_ENROLLED_CLASS_DATA,
  UPDATE_SIMPLE_ENROLLED_CLASS_DATA,
} from './actions';
import { currentUserIdSelector } from './selectors';
import { makeMetricsIdentify } from '../Metrics/manualEventDispatchers';
import { PLATFORM } from '../location/constants';

export const CREATE_USER_TAB_VIEW = 'signup';
export const LOGIN_USER_TAB_VIEW = 'login';
export const RESET_PASSWORD_TAB_VIEW = 'resetpassword';

// ================== COMMON
export function isLoggedIn(state) {
  return state.Auth.get('currentUser') !== null;
}

export function commonGetEnrolledClassData() {
  return (dispatch, getState) => {
    const currentUserId = currentUserIdSelector(getState());
    const currentEnrolments = getState().Enrolments;
    if (currentUserId !== null) {
      dispatch({
        type: START_FETCH_ENROLLED_CLASS_DATA,
      });
      return Resource.read('user', currentUserId, 'getEnrolledClassData').then(
        (data) => {
          const {
            enrolments,
            users2,
            classes,
            courses,
            qualificationEnrolments,
            institutions,
          } = data;

          dispatch({
            type: UPDATE_ENROLLED_CLASS_DATA,
            enrolments: Object.values(enrolments),
            users: Object.values(users2),
            cohortList: Object.values(classes),
            courses: Object.values(courses),
            qualificationEnrolments: Object.values(qualificationEnrolments),
            institutions: Object.values(institutions),
            currentUserId,
          });
          dispatch({
            type: SET_ENROLMENTS_HISTORY,
            enrolments: currentEnrolments,
          });

          const user = users2[currentUserId];
          if (user) {
            // This is to fix a bug with a past implementation of identify()
            // where isOLStaff was being populated with isSuperUser instead of isStaff
            // if we don't set it here, analytics.js (Segment.io client library)
            // will keep picking up and sending the stale value from local storage
            // TODO: move this to a more sensible location
            // (OR remove this code when it is no longer required ... in a month?)
            if (PLATFORM === 'web') {
              dispatch(
                makeMetricsIdentify(currentUserId, { isOLStaff: user.isStaff })
              );
            }
          }

          return Promise.resolve(data);
        }
      );
    } else {
      return Promise.resolve();
    }
  };
}

export function getCourseMembershipData() {
  return (dispatch, getState) => {
    const currentUserId = currentUserIdSelector(getState());
    if (currentUserId !== null) {
      dispatch({
        type: START_FETCH_SIMPLE_ENROLLED_CLASS_DATA,
      });
      return Resource.read('user', currentUserId, 'getCourseMembershipData')
        .then((data) => {
          const {
            users,
            courses,
            qualificationEnrolments,
            institutions,
          } = data;
          dispatch({
            type: UPDATE_SIMPLE_ENROLLED_CLASS_DATA,
            users: Object.values(users),
            courses: Object.values(courses),
            qualificationEnrolments: Object.values(qualificationEnrolments),
            institutions: Object.values(institutions),
          });
          return Promise.resolve(data);
        })
        .catch(() => {
          return Promise.resolve();
        });
    } else {
      return Promise.resolve();
    }
  };
}

export function hideLoginError() {
  return (dispatch) => {
    dispatch({ type: SET_IN_PROGRESS_FALSE });
  };
}

export function stopLoadingLogin() {
  return (dispatch) => {
    dispatch({ type: SET_IN_PROGRESS_FALSE });
  };
}

const cookie = new Cookies();

const TAPFILIATE_COOKIE_KEY = 'tapfiliate';

function matchesCurrentAffiliate(newCookie) {
  const currentCookie = cookie.get(TAPFILIATE_COOKIE_KEY);
  return (
    currentCookie &&
    currentCookie.aid === newCookie.aid &&
    currentCookie.sid === newCookie.sid
  );
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * 60000);
}

function saveTapCookie(tapCookie) {
  const expires = addMinutes(new Date(), 30);
  cookie.set(TAPFILIATE_COOKIE_KEY, tapCookie, { path: '/', expires });
}

function extendCurrentAffiliateCookieExpiry() {
  const tapCookie = cookie.get(TAPFILIATE_COOKIE_KEY);
  saveTapCookie(tapCookie);
}

function sendReferralConversion(userId) {
  const tapCookie = cookie.get(TAPFILIATE_COOKIE_KEY);
  if (tapCookie.referralSent) {
    // don't send the referral conversion again
  } else {
    const externalID = `${userId}-${tapCookie.cid}`;
    const metaData = {
      user: userId,
      click: tapCookie.cid,
    };
    window.tap(
      'conversion',
      externalID,
      0,
      { meta_data: metaData },
      'referral'
    );
    tapCookie.referralSent = new Date();
    saveTapCookie(tapCookie);
  }
}

function getSSOContextCookie() {
  const ssoContext = cookie.get('sso_context');
  const ssoUserDefaultImg = cookie.get('sso_user_image');

  return {
    ssoContext: ssoContext || 'another site',
    ssoUserDefaultImg,
  };
}

function getSSODetailsCookie() {
  const ssoDetails = cookie
    .get('sso_details')
    .replace(/"/g, '')
    .replace(/\\054/g, ',')
    .split(',');

  return {
    fullName: ssoDetails[0],
    emailAddress: ssoDetails[1],
  };
}

export function getSSOContext() {
  const { ssoContext, ssoUserDefaultImg } = getSSOContextCookie();
  return (dispatch) => {
    dispatch({
      type: SSO_USER_CONTEXT,
      ssoContext,
      ssoUserDefaultImg,
    });
  };
}

export function getSSOUserDetails() {
  return (dispatch) => {
    dispatch({
      type: CURRENT_SSO_USER_DETAILS,
      currentUserSSODetails: getSSODetailsCookie(),
    });
  };
}

export function identifyReferral(currentUser) {
  if (window.tap) {
    const tapCookie = cookie.get(TAPFILIATE_COOKIE_KEY);
    if (tapCookie && currentUser) {
      sendReferralConversion(currentUser);
    }

    window.tap('detectClick', (click) => {
      const { tap_a, tap_s } = parseQueryString();
      const newCookie = {
        aid: tap_a,
        sid: tap_s,
        cid: click.id,
        referralSent: null,
      };

      if (matchesCurrentAffiliate(newCookie)) {
        extendCurrentAffiliateCookieExpiry();
      } else {
        saveTapCookie(newCookie);
      }
      if (currentUser) {
        sendReferralConversion(currentUser);
      }
    });
  }
}

export function setCurrentUser(currentUser) {
  return (dispatch) => {
    identifyReferral(currentUser);
    dispatch(makeMetricsIdentify(currentUser));
    dispatch({ type: SET_CURRENT_USER, currentUser });
  };
}

export function setCurrentUserAccess(access) {
  return (dispatch) => {
    dispatch({ type: SET_CURRENT_USER_ACCESS, access });
  };
}

export function setCurrentPortal(currentPortalName) {
  return (dispatch) => {
    dispatch({
      type: AUTH_SET_CURRENT_PORTAL_NAME,
      currentPortalName,
    });
  };
}

export function setCurrentCourse(coursePath) {
  return (dispatch) => {
    dispatch({
      type: AUTH_SET_CURRENT_COURSE,
      coursePath,
    });
  };
}

export function setCurrentCohort(cohortPath) {
  return (dispatch) => {
    dispatch({
      type: AUTH_SET_CURRENT_COHORT,
      cohortPath,
    });
  };
}

export function signUpRequired() {
  return (dispatch) => {
    const { emailAddress } = getSSODetailsCookie();

    Resource.lookup(USER_RESOURCE, 'resources', {
      initArgsList: [emailAddress],
      argName: 'emailAddress',
      ignoreMissing: true,
    }).then((result) => {
      dispatch({
        type: SIGNUP_USER_REQUIRED,
        signUpUserRequired: Immutable.fromJS(result).size === 0,
        ssoView:
          Immutable.fromJS(result).size === 0
            ? CREATE_USER_TAB_VIEW
            : LOGIN_USER_TAB_VIEW,
      });
    });
  };
}

export function fetchCurrentUserFrontChatData(currentCoursePath) {
  return (dispatch, getState) => {
    const state = getState();

    const currentUser = currentUserIdSelector(state);
    if (!currentUser) {
      return Promise.resolve({ success: false });
    }

    return ResourceApi.read(
      USER_RESOURCE,
      currentUser,
      'getFrontChatDataForCourseAdmin',
      {
        coursePath: currentCoursePath,
      }
    );
  };
}
